import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Cog,
  HelpCircle,
  Split,
  TreePine,
} from 'lucide-react';
import type { ProposalBuilderVersionDoc, RfqHubFileRelease, RfqPackage, RfqProject, RfqRequest, RfqSubmission, SupplierInvite, SupplierOrg, VendorWorkType } from '../../types';
import type { ProjectSiteAddressInput } from '../../utils/supplierShipTo';
import type { WorkTypeVendorSummary } from '../../utils/vendorAdminProjectSummary';
import { rfqProjectSupplierOrgIds } from '../../utils/rfqProjectSuppliers';
import {
  normalizeSupplierOrg,
  orgHasCapability,
  rfqStatusLabel,
  workTypeLabel,
} from '../../utils/vendorHelpers';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import {
  canApproveSupplierQuoteOnBehalf,
  canUploadSupplierQuoteOnBehalf,
} from '../../utils/staffSupplierQuoteAccess';
import { canShowCombinedQuoteSplitEntry } from '../../utils/combinedQuoteSplit';
import CombinedQuoteSplitModal from './CombinedQuoteSplitModal';
import './CombinedQuoteSplitModal.css';
import { subscribeRfqHubFileReleases } from '../../utils/rfqHubFileReleaseService';
import {
  approvedSubmissionIdsOnRfqProject,
  awardedReleaseKeysByOrg,
} from '../../utils/rfqApprovedQuoteCoverage';
import { isReferenceRelease } from '../../utils/rfqReleasePurpose';
import { subscribeRfqRequestsForDeal } from '../../utils/rfqRequests';
import { useRfqRequestsAccess } from '../../utils/rfqRequestsAccess';
import TimberRfqReleasePanel from './TimberRfqReleasePanel';
import TimberRfqSuppliersPanel from './TimberRfqSuppliersPanel';
import CncRfqReleasePanel from './CncRfqReleasePanel';
import CncRfqSuppliersPanel from './CncRfqSuppliersPanel';
import {
  countActiveQuotesForReleaseSelection,
  displayStatusForReleaseSelection,
  isSubmissionApprovedForRelease,
  PRE_PORTAL_RELEASE_ID,
  releaseIdsWithAwards,
  releaseVersionLabel,
  resolveDefaultRfqReleaseId,
} from '../../utils/timberRfqPrePortalVersion';
import {
  buildQuoteRejectionTargets,
  isRfqQuoteRejected,
  projectHasAwardedSupplier,
  type QuoteRejectionTarget,
} from '../../utils/rfqQuoteRejectionTargets';
import SupplierQuoteRejectionModal from './SupplierQuoteRejectionModal';
import type { ProposalNodeSet } from '../../types';

const HINT_VIEWPORT_MARGIN = 12;
const HINT_GAP = 8;

type HintCoords = {
  top: number;
  left: number;
  placement: 'above' | 'below';
};

type RfqHintContent = {
  lead: string;
  sections: { dt: string; dd: string }[];
};

const useRfqHintPopover = () => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<number | null>(null);
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<HintCoords>({ top: 0, left: 0, placement: 'below' });

  const positionPopover = useCallback(() => {
    const anchor = anchorRef.current;
    const popover = popoverRef.current;
    if (!anchor || !popover) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popRect = popover.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const spaceBelow = vh - anchorRect.bottom - HINT_VIEWPORT_MARGIN;
    const spaceAbove = anchorRect.top - HINT_VIEWPORT_MARGIN;
    const placement =
      spaceBelow >= popRect.height || spaceBelow >= spaceAbove ? 'below' : 'above';

    let top =
      placement === 'below'
        ? anchorRect.bottom + HINT_GAP
        : anchorRect.top - HINT_GAP;

    if (placement === 'below') {
      top = Math.min(top, vh - HINT_VIEWPORT_MARGIN - popRect.height);
    } else {
      top = Math.max(HINT_VIEWPORT_MARGIN + popRect.height, top);
    }

    let left = anchorRect.left;
    if (left + popRect.width > vw - HINT_VIEWPORT_MARGIN) {
      left = vw - HINT_VIEWPORT_MARGIN - popRect.width;
    }
    left = Math.max(HINT_VIEWPORT_MARGIN, left);

    setCoords({ top, left, placement });
  }, []);

  const show = () => {
    if (hideTimerRef.current != null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({
      left: Math.max(HINT_VIEWPORT_MARGIN, rect.left),
      top: rect.bottom + HINT_GAP,
      placement: 'below',
    });
    setVisible(true);
  };

  const hide = () => {
    hideTimerRef.current = window.setTimeout(() => setVisible(false), 100);
  };

  useEffect(() => {
    return () => {
      if (hideTimerRef.current != null) window.clearTimeout(hideTimerRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    if (!visible) return;
    positionPopover();
  }, [visible, positionPopover]);

  useEffect(() => {
    if (!visible) return;
    const onReflow = () => positionPopover();
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [visible, positionPopover]);

  return { anchorRef, popoverRef, visible, coords, show, hide };
};

const RfqHintButton: React.FC<{ ariaLabel: string; content: RfqHintContent }> = ({
  ariaLabel,
  content,
}) => {
  const { anchorRef, popoverRef, visible, coords, show, hide } = useRfqHintPopover();

  return (
    <>
      <div
        ref={anchorRef}
        className="srfq-timber-hint-anchor"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <button type="button" className="srfq-timber-hint-btn" tabIndex={0} aria-label={ariaLabel}>
          <HelpCircle size={14} aria-hidden />
        </button>
      </div>
      {visible &&
        createPortal(
          <div
            ref={popoverRef}
            className={`srfq-timber-hint-popover srfq-timber-hint-popover--fixed srfq-timber-hint-popover--${coords.placement}`}
            style={{ top: coords.top, left: coords.left }}
            role="tooltip"
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <p className="srfq-timber-hint-lead">{content.lead}</p>
            <dl className="srfq-timber-hint-list">
              {content.sections.map(({ dt, dd }) => (
                <div key={dt}>
                  <dt>{dt}</dt>
                  <dd>{dd}</dd>
                </div>
              ))}
            </dl>
          </div>,
          document.body,
        )}
    </>
  );
};

const TIMBER_HINT: RfqHintContent = {
  lead: 'Send version-controlled timber lists to suppliers and collect quotes for this project.',
  sections: [
    {
      dt: 'Timber lists',
      dd: 'Staged from Project Hub → Files when staff release a CSV. Compare each version to the live proposal, then publish to the supplier portal.',
    },
    {
      dt: 'Suppliers',
      dd: 'Timber vendors invited to bid on the selected list. Species badges reflect what each supplier quotes in the materials database.',
    },
  ],
};

const CNC_HINT: RfqHintContent = {
  lead: 'Send staged .3d models to CNC suppliers and collect machine quotes for this project.',
  sections: [
    {
      dt: 'Model releases',
      dd: 'Staged from Project Hub → Files (3D track). Compare versions, then publish to the supplier portal.',
    },
    {
      dt: 'Suppliers',
      dd: 'CNC-capable vendors invited to bid. Species badges reflect what each supplier should quote.',
    },
  ],
};

export interface VendorWorkTypeSectionProps {
  workType: VendorWorkType;
  dealId: string;
  project: RfqProject | null;
  workSummary: WorkTypeVendorSummary;
  supplierOrgs: SupplierOrg[];
  supplierOrgById: Map<string, SupplierOrg>;
  rfqPackages: RfqPackage[];
  rfqSubmissions: RfqSubmission[];
  versionsByDeal: Record<string, ProposalBuilderVersionDoc[]>;
  publishBusyId: string | null;
  packageTitleDraftByProject: Record<string, string>;
  versionPickByProject: Record<string, string>;
  onUpdateProject: (id: string, updates: Partial<RfqProject>) => Promise<void>;
  onSyncHubLink: (proj: RfqProject) => void;
  onRequestPublish: (proj: RfqProject, targets: string[], pickOverride?: string) => void;
  /** Timber RFQ — open review modal before publishing a staged release. */
  onRequestTimberPublishReview?: (
    proj: RfqProject,
    targets: string[],
    release: RfqHubFileRelease,
    preload: { nodeSets: ProposalNodeSet[]; sourceFileName: string },
  ) => void;
  /** CNC quote — open review modal before publishing a staged release. */
  onRequestCncPublishReview?: (
    proj: RfqProject,
    targets: string[],
    release: RfqHubFileRelease,
  ) => void;
  /** Resend to a supplier whose structure scope changed after publish (same file). */
  onResendScopeChange?: (
    proj: RfqProject,
    orgId: string,
    release: RfqHubFileRelease,
    workType: 'timber_rfq' | 'cnc_quote',
  ) => void;
  /** Dismiss scope-change reminder without emailing the supplier. */
  onDismissScopeChange?: (
    proj: RfqProject,
    orgId: string,
    release: RfqHubFileRelease,
    workType: 'timber_rfq' | 'cnc_quote',
  ) => Promise<boolean>;
  onDeletePackage: (packageId: string) => void;
  onOpenSubmission: (sub: RfqSubmission) => void;
  onDeleteSubmission?: (sub: RfqSubmission, project: RfqProject) => void | Promise<void>;
  onLoadVersions: (dealId: string) => void;
  onPackageTitleChange: (projectId: string, title: string) => void;
  onVersionPickChange: (projectId: string, pick: string) => void;
  /** Timber RFQ on the same deal — CNC inherits species for dual-capability suppliers. */
  timberRfqProject?: RfqProject | null;
  /** CNC RFQ on the same deal — timber delivery defaults when supplier is on both work types. */
  cncRfqProject?: RfqProject | null;
  projectSite: ProjectSiteAddressInput;
  supplierInvites: SupplierInvite[];
  projectNumber?: string;
  projectName?: string;
  /** Project Hub timber project PM uid — also enables quote upload for that PM. */
  hubProjectManagerUid?: string;
  /** Timber RFQ — open Files → RFQs request wizard with these staged releases preselected. */
  onOpenFilesRequest?: (releaseIds: string[]) => void;
}

const VendorWorkTypeSection: React.FC<VendorWorkTypeSectionProps> = ({
  workType,
  dealId,
  project,
  workSummary,
  supplierOrgs,
  supplierOrgById,
  rfqPackages,
  rfqSubmissions,
  publishBusyId,
  packageTitleDraftByProject,
  onUpdateProject,
  onSyncHubLink,
  onRequestTimberPublishReview,
  onRequestCncPublishReview,
  onResendScopeChange,
  onDismissScopeChange,
  onOpenSubmission,
  onDeleteSubmission,
  onPackageTitleChange,
  timberRfqProject,
  cncRfqProject,
  projectSite,
  supplierInvites,
  projectNumber,
  projectName,
  hubProjectManagerUid,
  onOpenFilesRequest,
}) => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const isCnc = workType === 'cnc_quote';
  const rfqRequestsGate = useRfqRequestsAccess();
  const [rfqRequests, setRfqRequests] = useState<RfqRequest[]>([]);
  useEffect(() => {
    if (isCnc || !dealId || !rfqRequestsGate.enabled) {
      setRfqRequests([]);
      return;
    }
    return subscribeRfqRequestsForDeal(dealId, setRfqRequests, () => setRfqRequests([]));
  }, [isCnc, dealId, rfqRequestsGate.enabled]);
  const SectionIcon = isCnc ? Cog : TreePine;

  const canStaffUploadQuote = useMemo(() => {
    const row = crmUsers.find((u) => u.uid === user?.id);
    return canUploadSupplierQuoteOnBehalf({
      authRole: user?.role,
      designations: row?.designations,
      hubProjectManagerUid,
      userId: user?.id,
    });
  }, [user, crmUsers, hubProjectManagerUid]);
  const canApproveSplit = useMemo(() => {
    const row = crmUsers.find((u) => u.uid === user?.id);
    return canApproveSupplierQuoteOnBehalf({
      authRole: user?.role,
      designations: row?.designations,
      hubProjectManagerUid,
      userId: user?.id,
    });
  }, [user, crmUsers, hubProjectManagerUid]);
  const [hubFileReleases, setHubFileReleases] = useState<RfqHubFileRelease[]>([]);
  const [selectedTimberReleaseId, setSelectedTimberReleaseId] = useState('');
  const [selectedCncReleaseId, setSelectedCncReleaseId] = useState('');
  const userPickedTimberReleaseRef = useRef(false);
  const userPickedCncReleaseRef = useRef(false);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [rejectionModalTargets, setRejectionModalTargets] = useState<QuoteRejectionTarget[] | null>(
    null,
  );
  const rejectionAfterCloseRef = useRef<(() => void) | null>(null);
  const [emptyStateSplitTarget, setEmptyStateSplitTarget] = useState<{
    sub: RfqSubmission;
    orgId: string;
    orgName: string;
  } | null>(null);
  const projectSupplierOrgIds = useMemo(
    () => (project ? rfqProjectSupplierOrgIds(project) : []),
    [project],
  );

  const timberCapableOrgs = useMemo(
    () => supplierOrgs.filter((o) => o.id && orgHasCapability(normalizeSupplierOrg(o), 'timber')),
    [supplierOrgs],
  );

  const cncCapableOrgs = useMemo(
    () => supplierOrgs.filter((o) => o.id && orgHasCapability(normalizeSupplierOrg(o), 'cnc')),
    [supplierOrgs],
  );

  const cncPublishTargets = useMemo(() => {
    return projectSupplierOrgIds.filter((id) => {
      const org = supplierOrgById.get(id);
      return orgHasCapability(org ? normalizeSupplierOrg(org) : null, 'cnc');
    });
  }, [projectSupplierOrgIds, supplierOrgById]);

  useEffect(() => {
    if (!project?.id) {
      setHubFileReleases([]);
      return;
    }
    return subscribeRfqHubFileReleases(project.id, setHubFileReleases);
  }, [project?.id]);

  const projectPackages = useMemo(() => {
    if (!project?.id) return [];
    return rfqPackages
      .filter((p) => p.rfqProjectId === project.id)
      .slice()
      .sort((a, b) => {
        const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return tb - ta;
      });
  }, [rfqPackages, project?.id]);

  const orgSubmissionsByPackageId = useMemo(() => {
    const m = new Map<string, RfqSubmission[]>();
    if (!project?.id) return m;
    rfqSubmissions
      .filter((s) => s.rfqProjectId === project.id && s.rfqPackageId)
      .forEach((s) => {
        const pid = s.rfqPackageId as string;
        const list = m.get(pid) || [];
        list.push(s);
        m.set(pid, list);
      });
    m.forEach((list) => {
      list.sort((a, b) => {
        const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return tb - ta;
      });
    });
    return m;
  }, [rfqSubmissions, project?.id]);

  /** Qualifying timber quotes when CNC work order does not exist yet (Georgian II-style). */
  const emptyStateSplitCandidates = useMemo(() => {
    if (!isCnc || project || !timberRfqProject?.id || !canApproveSplit) return [];
    const timberPackages = rfqPackages.filter((p) => p.rfqProjectId === timberRfqProject.id);
    const timberSubs = rfqSubmissions.filter((s) => s.rfqProjectId === timberRfqProject.id);
    const submissionsByPackageId = new Map<string, RfqSubmission[]>();
    for (const sub of timberSubs) {
      if (!sub.rfqPackageId) continue;
      const list = submissionsByPackageId.get(sub.rfqPackageId) ?? [];
      list.push(sub);
      submissionsByPackageId.set(sub.rfqPackageId, list);
    }
    const byOrg = new Map<string, RfqSubmission>();
    for (const sub of timberSubs) {
      if (!sub.supplierOrgId || sub.notBidding || isRfqQuoteRejected(sub)) continue;
      if (!sub.storagePath && !sub.cncQuote) continue;
      const orgId = sub.supplierOrgId;
      const isQuoteApproved = !!sub.id
        && isSubmissionApprovedForRelease(
          timberRfqProject,
          timberPackages,
          submissionsByPackageId,
          PRE_PORTAL_RELEASE_ID,
          sub.id,
        );
      if (
        !canShowCombinedQuoteSplitEntry({
          canApprove: canApproveSplit,
          isQuoteApproved,
          orgId,
          org: supplierOrgById.get(orgId),
          timberProject: timberRfqProject,
          cncProject: null,
          timberSubmission: sub,
          cncSubmissionsForOrg: [],
        })
      ) {
        continue;
      }
      const existing = byOrg.get(orgId);
      if (!existing) {
        byOrg.set(orgId, sub);
        continue;
      }
      const existingTs = existing.createdAt instanceof Date ? existing.createdAt.getTime() : 0;
      const nextTs = sub.createdAt instanceof Date ? sub.createdAt.getTime() : 0;
      if (nextTs >= existingTs) byOrg.set(orgId, sub);
    }
    return Array.from(byOrg.entries()).map(([orgId, sub]) => ({
      orgId,
      orgName: supplierOrgById.get(orgId)?.name || orgId,
      sub,
    }));
  }, [
    isCnc,
    project,
    timberRfqProject,
    canApproveSplit,
    rfqPackages,
    rfqSubmissions,
    supplierOrgById,
  ]);

  const emptyStateTimberPackages = useMemo(() => {
    if (!timberRfqProject?.id) return [];
    return rfqPackages.filter((p) => p.rfqProjectId === timberRfqProject.id);
  }, [rfqPackages, timberRfqProject?.id]);

  const emptyStateTimberSubmissionsByPackageId = useMemo(() => {
    const m = new Map<string, RfqSubmission[]>();
    if (!timberRfqProject?.id) return m;
    for (const sub of rfqSubmissions) {
      if (sub.rfqProjectId !== timberRfqProject.id || !sub.rfqPackageId) continue;
      const list = m.get(sub.rfqPackageId) ?? [];
      list.push(sub);
      m.set(sub.rfqPackageId, list);
    }
    return m;
  }, [rfqSubmissions, timberRfqProject?.id]);

  const stagedTimberReleases = useMemo(
    () =>
      hubFileReleases
        .filter((r) => r.workType === 'timber_rfq')
        .slice()
        .sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex),
    [hubFileReleases],
  );

  const stagedCncReleases = useMemo(
    () =>
      hubFileReleases
        .filter((r) => r.workType === 'cnc_quote' && r.sourceStreamKind === 'three_d_raw')
        .slice()
        .sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex),
    [hubFileReleases],
  );

  useEffect(() => {
    userPickedTimberReleaseRef.current = false;
    userPickedCncReleaseRef.current = false;
  }, [project?.id]);

  const handleTimberReleaseChange = useCallback((releaseId: string) => {
    userPickedTimberReleaseRef.current = true;
    setSelectedTimberReleaseId(releaseId);
  }, []);

  const handleCncReleaseChange = useCallback((releaseId: string) => {
    userPickedCncReleaseRef.current = true;
    setSelectedCncReleaseId(releaseId);
  }, []);

  useEffect(() => {
    if (isCnc || userPickedTimberReleaseRef.current || !project) return;
    setSelectedTimberReleaseId(prev =>
      resolveDefaultRfqReleaseId({
        prev,
        stagedReleaseIds: stagedTimberReleases.map(r => r.id).filter((id): id is string => !!id),
        packages: projectPackages,
        submissionsByPackageId: orgSubmissionsByPackageId,
        project,
        workType: 'timber_rfq',
      }),
    );
  }, [isCnc, stagedTimberReleases, projectPackages, orgSubmissionsByPackageId, project]);

  useEffect(() => {
    if (!isCnc || userPickedCncReleaseRef.current || !project) return;
    setSelectedCncReleaseId(prev =>
      resolveDefaultRfqReleaseId({
        prev,
        stagedReleaseIds: stagedCncReleases.map(r => r.id).filter((id): id is string => !!id),
        packages: projectPackages,
        submissionsByPackageId: orgSubmissionsByPackageId,
        project,
        workType: 'cnc_quote',
      }),
    );
  }, [isCnc, stagedCncReleases, projectPackages, orgSubmissionsByPackageId, project]);

  const selectedReleaseId = isCnc ? selectedCncReleaseId : selectedTimberReleaseId;

  const awardedReleaseIds = useMemo(() => {
    if (!project) return [] as string[];
    const staged = (isCnc ? stagedCncReleases : stagedTimberReleases)
      .map(r => r.id)
      .filter((id): id is string => !!id);
    return releaseIdsWithAwards(
      project,
      projectPackages,
      orgSubmissionsByPackageId,
      staged,
      isCnc ? 'cnc_quote' : 'timber_rfq',
    );
  }, [
    project,
    isCnc,
    stagedCncReleases,
    stagedTimberReleases,
    projectPackages,
    orgSubmissionsByPackageId,
  ]);

  const awardedReleaseIdSet = useMemo(() => new Set(awardedReleaseIds), [awardedReleaseIds]);

  // Suppliers holding an approved quote on this work order. Reference releases
  // (refined files after an award) are shared with these suppliers only — nobody
  // is asked to requote.
  const awardedOrgIds = useMemo(() => {
    if (!project) return new Set<string>();
    const out = new Set(Array.from(awardedReleaseKeysByOrg(project).keys()));
    const approvedSubIds = approvedSubmissionIdsOnRfqProject(project);
    orgSubmissionsByPackageId.forEach(subs => {
      for (const sub of subs) {
        if (sub.id && approvedSubIds.has(sub.id) && sub.supplierOrgId) out.add(sub.supplierOrgId);
      }
    });
    return out;
  }, [project, orgSubmissionsByPackageId]);

  const publishTargetsForRelease = useCallback(
    (release: RfqHubFileRelease | undefined, base: string[]): string[] => {
      if (!release || !isReferenceRelease(release)) return base;
      const awarded = base.filter(id => awardedOrgIds.has(id));
      return awarded.length ? awarded : base;
    },
    [awardedOrgIds],
  );
  const referenceTargetOrgIds = useMemo(
    () => (isCnc ? cncPublishTargets : projectSupplierOrgIds).filter(id => awardedOrgIds.has(id)),
    [isCnc, cncPublishTargets, projectSupplierOrgIds, awardedOrgIds],
  );

  const alternateAwardRelease = useMemo(() => {
    if (!selectedReleaseId || awardedReleaseIdSet.has(selectedReleaseId)) return null;
    const otherId = awardedReleaseIds[0];
    if (!otherId) return null;
    if (otherId === PRE_PORTAL_RELEASE_ID) {
      return { id: otherId, label: releaseVersionLabel(0) };
    }
    const staged = (isCnc ? stagedCncReleases : stagedTimberReleases).find(r => r.id === otherId);
    return {
      id: otherId,
      label: releaseVersionLabel(staged?.releaseVersionIndex ?? 0),
    };
  }, [
    selectedReleaseId,
    awardedReleaseIdSet,
    awardedReleaseIds,
    isCnc,
    stagedCncReleases,
    stagedTimberReleases,
  ]);

  const releaseQuoteCount = useMemo(
    () =>
      countActiveQuotesForReleaseSelection(
        projectPackages,
        orgSubmissionsByPackageId,
        selectedReleaseId || null,
      ),
    [projectPackages, orgSubmissionsByPackageId, selectedReleaseId],
  );

  const releaseDisplayStatus = useMemo(
    () =>
      displayStatusForReleaseSelection(
        project?.status,
        projectPackages,
        orgSubmissionsByPackageId,
        selectedReleaseId || null,
      ),
    [project?.status, projectPackages, orgSubmissionsByPackageId, selectedReleaseId],
  );

  const headerQuoteCount = selectedReleaseId ? releaseQuoteCount : workSummary.quoteCount;
  const headerStatus = selectedReleaseId ? releaseDisplayStatus : project?.status;

  const rejectionTargets = useMemo(() => {
    if (!project?.id) return [];
    return buildQuoteRejectionTargets({
      workType,
      project,
      projectSupplierOrgIds,
      packages: projectPackages,
      submissionsByPackageId: orgSubmissionsByPackageId,
      selectedReleaseId: selectedReleaseId || null,
      supplierOrgById,
    });
  }, [
    project,
    selectedReleaseId,
    workType,
    projectSupplierOrgIds,
    projectPackages,
    orgSubmissionsByPackageId,
    supplierOrgById,
  ]);

  const rejectionTargetOrgIds = useMemo(
    () => new Set(rejectionTargets.map(t => t.orgId)),
    [rejectionTargets],
  );

  const hasAwardedSupplier = useMemo(
    () =>
      !!project
      && projectHasAwardedSupplier(
        project,
        projectPackages,
        orgSubmissionsByPackageId,
        workType,
      ),
    [project, projectPackages, orgSubmissionsByPackageId, workType],
  );

  const openRejectionModal = useCallback(
    (orgId?: string) => {
      if (orgId) {
        const orgTargets = rejectionTargets.filter(t => t.orgId === orgId);
        if (!orgTargets.length) return;
        setRejectionModalTargets(orgTargets);
      } else {
        setRejectionModalTargets(null);
      }
      setRejectionModalOpen(true);
    },
    [rejectionTargets],
  );

  const requestRejectionNotices = useCallback(
    (targets: QuoteRejectionTarget[], afterClose?: () => void) => {
      if (!targets.length) {
        afterClose?.();
        return;
      }
      rejectionAfterCloseRef.current = afterClose ?? null;
      setRejectionModalTargets(targets);
      setRejectionModalOpen(true);
    },
    [],
  );

  return (
    <section className="srfq-work-type-section" id={workType} aria-labelledby={`${workType}-heading`}>
      <header className="srfq-work-type-section-head">
        <h3 id={`${workType}-heading`} className="srfq-work-type-section-title">
          <SectionIcon size={16} aria-hidden />
          {workTypeLabel(workType)}
          <RfqHintButton
            ariaLabel={isCnc ? 'About CNC quote' : 'About timber RFQ'}
            content={isCnc ? CNC_HINT : TIMBER_HINT}
          />
        </h3>
        <div className="srfq-work-type-section-meta">
          {project && headerStatus && (
            <span className="ops-stat ops-stat-active">{rfqStatusLabel(headerStatus)}</span>
          )}
          {workSummary.suppliers.length > 0 && (
            <span className="ops-stat">
              {workSummary.suppliers.length} supplier{workSummary.suppliers.length === 1 ? '' : 's'}
            </span>
          )}
          {headerQuoteCount > 0 && (
            <span className="ops-stat ops-stat-done">
              {headerQuoteCount} quote{headerQuoteCount === 1 ? '' : 's'}
            </span>
          )}
          {rejectionTargets.length > 0 && (
            <button
              type="button"
              className="ops-view-btn srfq-rejection-notices-btn"
              onClick={() => openRejectionModal()}
            >
              {hasAwardedSupplier
                ? `Notify rejected suppliers (${rejectionTargets.length})`
                : `Decline pre-portal quotes (${rejectionTargets.length})`}
            </button>
          )}
        </div>
      </header>

      {alternateAwardRelease && (
        <div className="srfq-award-release-hint" role="status">
          <span>
            Approved quotes are on {alternateAwardRelease.label}
            {selectedReleaseId ? ' — this version does not show them.' : '.'}
          </span>
          <button
            type="button"
            className="ops-view-btn srfq-award-release-hint-btn"
            onClick={() =>
              isCnc
                ? handleCncReleaseChange(alternateAwardRelease.id)
                : handleTimberReleaseChange(alternateAwardRelease.id)
            }
          >
            View {alternateAwardRelease.label}
          </button>
        </div>
      )}

      {!project ? (
        <div className="srfq-work-type-empty">
          <SectionIcon size={24} strokeWidth={1.25} aria-hidden />
          <p>No {workTypeLabel(workType).toLowerCase()} work order yet</p>
          <p className="srfq-work-type-empty-hint">
            Release a file from Project Hub → Files to start this work order.
          </p>
          {isCnc && emptyStateSplitCandidates.length > 0 && (
            <div className="srfq-work-type-empty-split">
              {emptyStateSplitCandidates.map(({ orgId, orgName, sub }) => (
                <button
                  key={`${orgId}-${sub.id || sub.storagePath}`}
                  type="button"
                  className="cqs-split-cue-btn"
                  title={`Split CNC dollars out of ${orgName}'s combined timber quote`}
                  onClick={() => setEmptyStateSplitTarget({ sub, orgId, orgName })}
                >
                  <Split size={12} aria-hidden />
                  Split CNC from timber quote
                  {emptyStateSplitCandidates.length > 1 ? ` (${orgName})` : ''}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : isCnc ? (
        <>
          <div className="srfq-timber-body srfq-timber-columns">
            <div className="srfq-timber-col srfq-timber-col--releases">
              <CncRfqReleasePanel
                project={project}
                publishTargetOrgIds={cncPublishTargets}
                releases={stagedCncReleases}
                packages={projectPackages}
                selectedReleaseId={selectedCncReleaseId}
                onSelectedReleaseChange={handleCncReleaseChange}
                awardedReleaseIds={awardedReleaseIdSet}
                referenceTargetOrgIds={referenceTargetOrgIds}
                publishBusy={publishBusyId === project.id}
                canPublish={cncPublishTargets.length > 0}
                onSyncHubLink={onSyncHubLink}
                onRequestPublishReview={(release) =>
                  onRequestCncPublishReview?.(project, publishTargetsForRelease(release, cncPublishTargets), release)
                }
              />
            </div>

            <div className="srfq-timber-col srfq-timber-col--suppliers">
              <CncRfqSuppliersPanel
                project={project}
                projectSupplierOrgIds={projectSupplierOrgIds}
                workSummary={workSummary}
                supplierOrgById={supplierOrgById}
                cncCapableOrgs={cncCapableOrgs}
                projectSite={projectSite}
                supplierInvites={supplierInvites}
                projectNumber={projectNumber}
                projectName={projectName}
                packages={projectPackages}
                submissionsByPackageId={orgSubmissionsByPackageId}
                selectedReleaseId={selectedCncReleaseId || null}
                hubReleases={stagedCncReleases}
                canStaffUploadQuote={canStaffUploadQuote}
                publishBusy={publishBusyId === project.id}
                onSendQuoteRequest={orgIds => {
                  const release = stagedCncReleases.find(r => r.id === selectedCncReleaseId);
                  if (!release?.id) return;
                  if (isReferenceRelease(release) && !orgIds.some(id => awardedOrgIds.has(id))) {
                    alert(
                      'Reference releases are shared only with suppliers who hold an approved quote on this work order. '
                        + 'Stage a quoting release instead if this supplier should price the model.',
                    );
                    return;
                  }
                  onRequestCncPublishReview?.(project, publishTargetsForRelease(release, orgIds), release);
                }}
                onResendScopeChange={orgId => {
                  const release = stagedCncReleases.find(r => r.id === selectedCncReleaseId);
                  if (!release?.id) return;
                  onResendScopeChange?.(project, orgId, release, 'cnc_quote');
                }}
                onDismissScopeChange={
                  onDismissScopeChange
                    ? async orgId => {
                        const release = stagedCncReleases.find(r => r.id === selectedCncReleaseId);
                        if (!release?.id) return false;
                        return onDismissScopeChange(project, orgId, release, 'cnc_quote');
                      }
                    : undefined
                }
                rejectionTargetOrgIds={rejectionTargetOrgIds}
                onRejectQuote={openRejectionModal}
                onRequestRejectionNotices={requestRejectionNotices}
                timberRfqProject={timberRfqProject}
                hubProjectManagerUid={hubProjectManagerUid}
                onUpdateProject={onUpdateProject}
                onOpenSubmission={onOpenSubmission}
                onDeleteSubmission={onDeleteSubmission}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="srfq-timber-body srfq-timber-columns">
          <div className="srfq-timber-col srfq-timber-col--releases">
            <TimberRfqReleasePanel
              dealId={dealId}
              rfqProjectId={project.id}
              publishTargetOrgIds={projectSupplierOrgIds}
              releases={stagedTimberReleases}
              packages={projectPackages}
              selectedReleaseId={selectedTimberReleaseId}
              onSelectedReleaseChange={handleTimberReleaseChange}
              awardedReleaseIds={awardedReleaseIdSet}
              referenceTargetOrgIds={referenceTargetOrgIds}
              publishBusy={publishBusyId === project.id}
              canPublish={projectSupplierOrgIds.length > 0}
              onRequestPublishReview={(release, preload) =>
                onRequestTimberPublishReview?.(
                  project,
                  publishTargetsForRelease(release, projectSupplierOrgIds),
                  release,
                  preload,
                )
              }
              requests={rfqRequests}
              requestsEnabled={rfqRequestsGate.enabled}
              onOpenFilesRequest={onOpenFilesRequest}
            />
          </div>

          <div className="srfq-timber-col srfq-timber-col--suppliers">
            <TimberRfqSuppliersPanel
              project={project}
              projectSupplierOrgIds={projectSupplierOrgIds}
              workSummary={workSummary}
              supplierOrgById={supplierOrgById}
              timberCapableOrgs={timberCapableOrgs}
              projectSite={projectSite}
              supplierInvites={supplierInvites}
              projectNumber={projectNumber}
              projectName={projectName}
              packages={projectPackages}
              submissionsByPackageId={orgSubmissionsByPackageId}
              selectedReleaseId={selectedTimberReleaseId || null}
              hubReleases={stagedTimberReleases}
              canStaffUploadQuote={canStaffUploadQuote}
              publishBusy={publishBusyId === project.id}
              onSendQuoteRequest={orgIds => {
                const release = stagedTimberReleases.find(r => r.id === selectedTimberReleaseId);
                if (!release?.id) return;
                if (isReferenceRelease(release) && !orgIds.some(id => awardedOrgIds.has(id))) {
                  alert(
                    'Reference releases are shared only with suppliers who hold an approved quote on this work order. '
                      + 'Stage a quoting release instead if this supplier should price the list.',
                  );
                  return;
                }
                onRequestTimberPublishReview?.(
                  project,
                  publishTargetsForRelease(release, orgIds),
                  release,
                  { nodeSets: [], sourceFileName: '' },
                );
              }}
              onResendScopeChange={orgId => {
                const release = stagedTimberReleases.find(r => r.id === selectedTimberReleaseId);
                if (!release?.id) return;
                onResendScopeChange?.(project, orgId, release, 'timber_rfq');
              }}
              onDismissScopeChange={
                onDismissScopeChange
                  ? async orgId => {
                      const release = stagedTimberReleases.find(r => r.id === selectedTimberReleaseId);
                      if (!release?.id) return false;
                      return onDismissScopeChange(project, orgId, release, 'timber_rfq');
                    }
                  : undefined
              }
              rejectionTargetOrgIds={rejectionTargetOrgIds}
              onRejectQuote={openRejectionModal}
              onRequestRejectionNotices={requestRejectionNotices}
              cncRfqProject={cncRfqProject}
              hubProjectManagerUid={hubProjectManagerUid}
              onUpdateProject={onUpdateProject}
              onOpenSubmission={onOpenSubmission}
              onDeleteSubmission={onDeleteSubmission}
            />
          </div>
        </div>
      )}

      {rejectionModalOpen && project && (
        <SupplierQuoteRejectionModal
          open
          targets={rejectionModalTargets ?? rejectionTargets}
          project={project}
          workType={workType}
          projectNumber={projectNumber}
          projectName={projectName}
          supplierOrgById={supplierOrgById}
          supplierInvites={supplierInvites}
          onClose={() => {
            setRejectionModalOpen(false);
            setRejectionModalTargets(null);
            const next = rejectionAfterCloseRef.current;
            rejectionAfterCloseRef.current = null;
            next?.();
          }}
        />
      )}

      {emptyStateSplitTarget && timberRfqProject?.id && (
        <CombinedQuoteSplitModal
          open
          timberSubmission={emptyStateSplitTarget.sub}
          timberProject={timberRfqProject}
          cncProject={null}
          orgId={emptyStateSplitTarget.orgId}
          orgName={emptyStateSplitTarget.orgName}
          packages={emptyStateTimberPackages}
          submissionsByPackageId={emptyStateTimberSubmissionsByPackageId}
          selectedTimberReleaseId={PRE_PORTAL_RELEASE_ID}
          hubProjectManagerUid={hubProjectManagerUid}
          onClose={() => setEmptyStateSplitTarget(null)}
        />
      )}

    </section>
  );
};

export default VendorWorkTypeSection;